import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { type Component, createResource, createSignal, Show } from 'solid-js'
import { z } from 'zod'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useForm } from '~/hooks/useForm'
import { usePermissions } from '~/hooks/usePermissions'
import { type ParameterType, parameterService } from '~/services/parameter.service'
import { P } from '~/utils/permissions'
import { generateSlug } from '~/utils/slug'

const ParametrosPage: Component = () => {
  const { can } = usePermissions()
  const alert = useAlert()
  const [search, debouncedSearch, setSearch] = useDebouncedSignal('')

  const [params, { refetch }] = createResource(debouncedSearch, async q => {
    try {
      const result = await parameterService.list(q ? { label: `%${q}%` } : undefined)
      return result.items
    } catch {
      return []
    }
  })

  const [showModal, setShowModal] = createSignal(false)
  const [editing, setEditing] = createSignal<ParameterType | null>(null)
  const toast = useToast()

  const parameterSchema = z.object({
    type: z.string().trim().min(1, 'El tipo es obligatorio'),
    key: z.string().trim().min(1, 'La clave es obligatoria'),
    label: z.string().trim().min(1, 'La etiqueta es obligatoria'),
    order: z.coerce.number().default(0)
  })

  const form = useForm({
    schema: parameterSchema,
    initialValues: () => ({
      type: editing()?.type ?? '',
      key: editing()?.key ?? '',
      label: editing()?.label ?? '',
      order: String(editing()?.order ?? 0)
    }),
    onSubmit: async values => {
      if (editing()) {
        await parameterService.update(editing()!.id, values)
        toast.addToast({ title: 'Parámetro actualizado', type: 'success' })
      } else {
        await parameterService.create(values)
        toast.addToast({ title: 'Parámetro creado', type: 'success' })
      }
      setShowModal(false)
      await refetch()
    },
    onError: err => toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
  })

  const openCreate = () => {
    setEditing(null)
    form.reset()
    setShowModal(true)
  }

  const openEdit = (item: ParameterType) => {
    setEditing(item)
    setShowModal(true)
  }

  const handleDelete = async (id: number) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar parámetro',
      message: '¿Estás seguro de eliminar este parámetro?'
    })
    if (!ok) return
    try {
      await parameterService.delete(id)
      toast.addToast({ title: 'Parámetro eliminado', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<number>>(new Set())

  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<number>()
    for (const value of selected.values()) next.add(value as number)
    setSelectedIds(next)
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Eliminar parámetros',
      message: `¿Eliminar ${ids.length} ${ids.length === 1 ? 'parámetro' : 'parámetros'}? Los globales se omiten a menos que seas super admin.`
    })
    if (!ok) return
    try {
      const res = await parameterService.bulkDelete(ids)
      const skippedNote = res.skipped > 0 ? ` (${res.skipped} globales omitidos)` : ''
      toast.addToast({ title: `${res.deleted} ${res.deleted === 1 ? 'parámetro eliminado' : 'parámetros eliminados'}${skippedNote}`, type: 'success' })
      setSelectedIds(new Set<number>())
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  const columns = [
    { header: 'Tipo', accessor: (p: ParameterType) => p.type },
    { header: 'Clave', accessor: (p: ParameterType) => p.key },
    { header: 'Etiqueta', accessor: (p: ParameterType) => p.label },
    { header: 'Orden', accessor: (p: ParameterType) => String(p.order) }
  ]

  return (
    <>
      <Title>Parámetros | Florez Jaramillo</Title>
      <Table<ParameterType>
        title="Parámetros"
        subtitle="Catálogos y parámetros del sistema"
        headerActions={
          <div class="flex items-center gap-2">
            <Input label="Buscar" value={search()} onInput={e => setSearch(e.currentTarget.value)} placeholder="Clave o etiqueta..." startIcon="material-symbols-light:search" class="min-w-48" />
            <Show when={can(P.parameters.create)}>
              <Button onClick={openCreate} prependIcon="material-symbols-light:add-circle-outline">
                Nuevo
              </Button>
            </Show>
          </div>
        }
        data={params() ?? []}
        columns={columns}
        keyField="id"
        headSticky
        loading={params.loading}
        selectable={can(P.parameters.delete)}
        selectableColor="danger"
        onSelectionChange={handleSelectionChange}
        onDeleteSelected={() => void handleBulkDelete()}
        emptyMessage="Aún no hay parámetros"
        actions={item => (
          <ButtonGroup size="sm" variant="soft">
            <Show when={can(P.parameters.update)}>
              <Button color="warning" onClick={() => openEdit(item)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
            </Show>
            <Show when={can(P.parameters.delete)}>
              <Button color="danger" onClick={() => handleDelete(item.id)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
            </Show>
          </ButtonGroup>
        )}
      />

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editing() ? 'Editar Parámetro' : 'Nuevo Parámetro'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="param-form" controller={form}>
            <div class="space-y-4">
              <Input name="type" label="Tipo" placeholder="ej: document_type" />
              <Input
                name="key"
                label="Clave"
                placeholder="ej: cc"
                endIcon={
                  <Button
                    size="sm"
                    variant="text"
                    icon="material-symbols-light:autorenew"
                    onClick={() => form.setValue('key', generateSlug(String(form.values().label ?? '')))}
                    tooltip="Generar clave desde la etiqueta"
                  />
                }
              />
              <Input name="label" label="Etiqueta" placeholder="ej: Cédula de Ciudadanía" />
              <Input name="order" label="Orden" type="number" />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={form.submitting() || !form.valid()} form="param-form">
              {form.submitting() ? 'Guardando...' : editing() ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default ParametrosPage
